import { i18nService } from '../../AppSettings/services/i18nService';
import Table from '../../Components/Table/Table';
import type { Column } from '../../Models/Table/TableModels';
import type { Employees, Employee, Permission, Rate } from '../../types/team';

interface TeamEmployeesTabProps {
  loading: boolean;
  employees: Employees[];
  currentUser: Employee | null;
  permissions: Permission[];
  rates: Rate[];
  activeDropdown: string | null;
  onDropdownToggle: (employeeId: string | null) => void;
  onPermissionUpdate: (employeeId: string, permishId: number) => void;
  onAssignRates: (employee: Employees) => void;
  /** Opens the Add-field-member modal (Build M) — rendered admin-only, like Actions. */
  onAddMember?: () => void;
  /** Opens the Enable-sign-in modal for a member (Build C) — the conversion path. */
  onEnableSignin?: (employee: Employees) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const TeamEmployeesTab = ({
  loading,
  employees,
  currentUser,
  permissions,
  rates,
  activeDropdown,
  onDropdownToggle,
  onPermissionUpdate,
  onAssignRates,
  onAddMember,
  onEnableSignin,
  currentPage,
  totalPages,
  onPageChange,
}: TeamEmployeesTabProps): JSX.Element => {
  const userPermishId = parseInt(String(currentUser?.permish_id));
  const isAdmin = userPermishId === 1;

  const getRateDisplay = (rateId: string | number | null | undefined): string => {
    if (!rateId) return 'Not assigned';
    const rate = rates.find((r) => r.rate_id === String(rateId));
    return rate ? `${rate.rate_title} ($${rate.rate}/hr)` : 'Not assigned';
  };

  const renderActionsButton = (employee: Employees): JSX.Element | null => {
    if (!isAdmin) {
      return null;
    }

    return (
      <div className="team-permissions-dropdown">
        <button
          className="team-action-button"
          onClick={(e) => {
            e.stopPropagation();
            onDropdownToggle(
              activeDropdown === employee.EmployeeID ? null : employee.EmployeeID
            );
          }}
        >
          Actions
        </button>
        {activeDropdown === employee.EmployeeID && (
          <div className="team-permissions-menu">
            <div className="team-rates-info">
              <div className="team-rates-info-title">Current Rates</div>
              <div className="team-rates-info-item">
                <span className="team-rates-info-label">Billed:</span>
                <span className="team-rates-info-value">{getRateDisplay(employee.billedrate)}</span>
              </div>
              <div className="team-rates-info-item">
                <span className="team-rates-info-label">Cost of Work:</span>
                <span className="team-rates-info-value">{getRateDisplay(employee.costofworkrate)}</span>
              </div>
              <div className="team-rates-info-item">
                <span className="team-rates-info-label">Field:</span>
                <span className="team-rates-info-value">{getRateDisplay(employee.feildrate)}</span>
              </div>
            </div>
            <div style={{ borderBottom: '1px solid #e2e8f0', margin: '0.5rem 0' }} />
            <div
              className="team-permissions-menu-item"
              onClick={() => {
                onAssignRates(employee);
                onDropdownToggle(null);
              }}
              style={{ fontWeight: 600, color: '#6b46c1' }}
            >
              Assign Rates
            </div>
            <div style={{ borderBottom: '1px solid #e2e8f0', margin: '0.25rem 0' }} />
            {/* A FIELD MEMBER cannot hold a permission (the server refuses with a 400) —
                offering the list would be offering a lie. Build C's conversion IS the way,
                so the menu offers exactly that. */}
            {employee.has_signin === false ? (
              onEnableSignin ? (
                <div
                  className="team-permissions-menu-item"
                  style={{ fontWeight: 600, color: '#6b46c1' }}
                  onClick={() => {
                    onEnableSignin(employee);
                    onDropdownToggle(null);
                  }}
                >
                  Enable sign-in…
                </div>
              ) : (
                <div className="team-permissions-menu-item team-member-no-permish">
                  Field member — enable sign-in to grant a role
                </div>
              )
            ) : (
              permissions.map((permission) => (
                <div
                  key={permission.permish_id}
                  className={`team-permissions-menu-item ${
                    employee.permish_id === permission.permish_id ? 'active' : ''
                  }`}
                  onClick={() =>
                    onPermissionUpdate(employee.EmployeeID, permission.permish_id)
                  }
                >
                  {permission.role}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  const columns: Column<Employees>[] = [
    {
      header: 'Name',
      accessor: 'FirstName',
      render: (row) =>
        `${row.FirstName} ${row.LastName}` ||
        i18nService.t('generic.notApplicable'),
    },
    {
      header: 'Employee ID',
      accessor: 'EmployeeID',
    },
    {
      header: 'Email',
      accessor: 'Email',
      render: (row) => (
        <span style={{ color: '#6559F6' }}>{row.Email || 'N/A'}</span>
      ),
    },
    {
      header: 'Phone',
      accessor: 'Phone',
      render: (row) => row.Phone || 'N/A',
    },
    {
      header: 'Role',
      accessor: 'role',
      // Plain text, no badge — the member disclosure follows the no-nag register.
      render: (row) =>
        row.has_signin === false ? 'Field member — no sign-in' : row.role || 'N/A',
    },
    ...(isAdmin
      ? [
          {
            header: 'Actions',
            render: (row: Employees) => renderActionsButton(row),
          },
        ]
      : []),
  ];

  if (loading) {
    return <div>{i18nService.t('team.employees.loadingEmployees')}</div>;
  }

  return (
    <>
      {isAdmin && onAddMember && (
        <div className="team-employees-toolbar">
          {/* Its OWN class, deliberately not team-action-button: the page's tests (and any
              future code) select the first action button as a ROW'S Actions. */}
          <button
            type="button"
            className="team-add-member-button"
            onClick={onAddMember}
          >
            + Add field member
          </button>
        </div>
      )}
      <Table
        columns={columns}
        data={employees}
        emptyMessage="No employees found"
        rowKey="EmployeeID"
      />

      <div className="team-pagination">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="team-pagination-button"
        >
          {i18nService.t('generic.buttons.previous')}
        </button>
        {[...Array(totalPages)].map((_, index) => (
          <button
            key={index + 1}
            onClick={() => onPageChange(index + 1)}
            className={`team-pagination-button ${
              currentPage === index + 1 ? 'active' : ''
            }`}
          >
            {index + 1}
          </button>
        ))}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="team-pagination-button"
        >
          {i18nService.t('generic.buttons.next')}
        </button>
      </div>
    </>
  );
};

export default TeamEmployeesTab;
